import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Fuel, ArrowLeft, Sun, Moon, ChevronDown, ChevronLeft, ChevronRight, X, Globe, CreditCard, Zap, Package, LogOut } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import FloatingLines from './reactbits/FloatingLines';
import GradientText from './reactbits/GradientText';
import SpotlightCard from './reactbits/SpotlightCard';
import { API_BASE } from './config';

const BILLING_API = `${API_BASE}/v1/billing`;

/* ─── Region detection + pack config ─── */
const detectRegion = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz.startsWith('Asia/') && (tz.includes('Kolkata') || tz.includes('Calcutta') || tz.includes('Mumbai')))
      return 'india';
    if (tz.startsWith('Australia/')) return 'au';
  } catch {}
  return 'us';
};

const REGION_CONFIG = {
  india: { label: 'India', flag: '\u{1F1EE}\u{1F1F3}', currency: '\u20B9', gateway: 'Razorpay' },
  us:    { label: 'US',    flag: '\u{1F1FA}\u{1F1F8}', currency: '$',      gateway: 'Stripe' },
  au:    { label: 'AU',    flag: '\u{1F1E6}\u{1F1FA}', currency: 'A$',     gateway: 'Stripe' },
};

const PACK_COLORS = {
  lite:       { accent: '#22d3ee', label: 'Lite', features: ['Single flow top-up', 'Full node library', 'Buy anytime'] },
  starter:    { accent: '#22c55e', label: 'Starter', features: ['3 permanent flows', 'Schema generation', 'Variable mapping'] },
  pro:        { accent: '#a855f7', label: 'Pro', features: ['10 permanent flows', 'Code export', 'Jira integration', 'Priority support'] },
  business:   { accent: '#f59e0b', label: 'Business', features: ['25 permanent flows', 'Everything in Pro', 'Team collaboration', 'Dedicated support'] },
  enterprise: { accent: '#ec4899', label: 'Enterprise', features: ['50 permanent flows', 'Everything in Business', 'Custom integrations', 'SLA guarantee'] },
};

// Ordered to match the flow lifecycle
const ACTION_LABELS = {
  jira_import: 'Jira Import',
  build_context: 'Build Context',
  questionnaire: 'Questionnaire',
  chat: 'Chat',
  create_flow: 'Create Flow',
  select_flow: 'Select Flow',
  map_variables: 'Map Variables',
  generate_prompts: 'Generate Prompts',
  test_prompt: 'Test Prompt',
  execute: 'Execute',
  generate_config: 'Generate Config',
  prototype: 'Prototype',
  prototype_ask: 'Prototype Help',
  code_export: 'Code Export',
  regenerate: 'Regenerate',
};

// Grouped actions matching the user-visible workflow steps
const ACTION_GROUPS = [
  { key: 'describe',    label: 'Describe',             icon: '💬', actions: ['jira_import', 'build_context', 'questionnaire'] },
  { key: 'chat',        label: 'Chat',                 icon: '💭', actions: ['chat'] },
  { key: 'design',      label: 'Design',               icon: '🔧', actions: ['create_flow', 'select_flow', 'regenerate'] },
  { key: 'map_data',    label: 'Map Data',             icon: '🔗', actions: ['map_variables'] },
  { key: 'prompts',     label: 'Prompts',              icon: '📝', actions: ['generate_prompts', 'test_prompt'] },
  { key: 'execute',     label: 'Execute',              icon: '⚡', actions: ['execute'] },
  { key: 'prototype',   label: 'Prototype',            icon: '🧪', actions: ['prototype', 'generate_config'] },
  { key: 'proto_exec',  label: 'Prototype Execution',  icon: '🚀', actions: ['prototype_ask'] },
  { key: 'export',      label: 'Export',               icon: '📦', actions: ['code_export'] },
];

// Estimated uses per action in a typical flow lifecycle (budget targets)
const EST_USES = {
  create_flow: 1,
  select_flow: 1,
  map_variables: 1,
  generate_prompts: 1,
  execute: 1,
  questionnaire: 4,   // Q1 gen, Q1 analyze, Q2 gen, finalize
  chat: 5,
  prototype: 1,
  generate_config: 1,
  prototype_ask: 1,
  code_export: 1,
  test_prompt: 1,
  regenerate: 1,
  jira_import: 1,
  build_context: 1,
};

/* ─── Per-flow budget vs actual card ─── */
function FlowBudgetCard({ title, totalSpent, actions, tokenCosts, flowBudget, dark }) {
  const getGroupBudget = (group) => {
    if (!tokenCosts) return 0;
    return group.actions.reduce((sum, a) => sum + (tokenCosts[a] || 0) * (EST_USES[a] || 1), 0);
  };

  const getGroupActual = (group) => {
    if (!actions) return 0;
    return group.actions.reduce((sum, a) => sum + (actions[a]?.tokens || 0), 0);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className={`text-base font-semibold ${dark ? 'text-gray-200' : 'text-gray-700'}`}>
          {title}
        </span>
        <span className={`text-sm tabular-nums font-medium ${dark ? 'text-cyan-400' : 'text-cyan-600'}`}>
          {totalSpent}/{flowBudget} tokens
        </span>
      </div>

      <div className="space-y-2.5">
        {ACTION_GROUPS.map(group => {
          const actual = getGroupActual(group);
          const budget = getGroupBudget(group);
          const barPct = budget > 0 ? Math.min((actual / budget) * 100, 100) : 0;
          const hasUsage = actual > 0;

          return (
            <div key={group.key} style={{ opacity: hasUsage ? 1 : (dark ? 0.45 : 0.7) }}>
              <div className="flex items-center justify-between mb-0.5">
                <span className={`text-sm flex items-center space-x-1.5 ${
                  dark ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  <span>{group.icon}</span>
                  <span>{group.label}</span>
                </span>
                <span className={`text-sm tabular-nums ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {actual}/{budget} tokens
                </span>
              </div>
              <div
                className="h-2 rounded-full overflow-hidden"
                style={{ background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}
              >
                {hasUsage && (
                  <div
                    className="h-full rounded-full transition-all duration-700 ease-out"
                    style={{
                      width: `${Math.max(barPct, 2)}%`,
                      background: '#22d3ee',
                      boxShadow: '0 0 6px rgba(34,211,238,0.3)',
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BillingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true;
  });
  const [balance, setBalance] = useState(null);
  const [tokenCosts, setTokenCosts] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const [lifetimePurchased, setLifetimePurchased] = useState(null);
  const [currentPack, setCurrentPack] = useState(null);

  const [flowUsage, setFlowUsage] = useState(null);
  const [agentNames, setAgentNames] = useState({});
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Packs modal state
  const [packsModalOpen, setPacksModalOpen] = useState(false);
  const [packsModalStep, setPacksModalStep] = useState('select'); // 'select' | 'confirm'
  const [packs, setPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(false);
  const [packsError, setPacksError] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(detectRegion);
  const [selectedPack, setSelectedPack] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [regionPickerOpen, setRegionPickerOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [userId, setUserId] = useState(null);

  const dark = isDarkMode;

  const toggleTheme = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  // Handle Stripe return (success or cancel)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const sessionId = params.get('session_id');
    const returnTo = localStorage.getItem('bytesized_billing_return');

    if (payment === 'success' && sessionId) {
      // Verify with backend and credit tokens (don't rely on webhook alone)
      (async () => {
        try {
          const { tokens: authTokens } = await fetchAuthSession();
          const authToken = authTokens?.idToken?.toString();
          const res = await fetch(`${BILLING_API}/verify-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: authToken } : {}) },
            body: JSON.stringify({ sessionId }),
          });
          if (res.ok) {
            const data = await res.json();
            if (data.status === 'credited') {
              setToastMessage(`Payment successful! ${data.tokens_added} tokens added.`);
            } else {
              setToastMessage('Payment successful! Your tokens have been added.');
            }
            // Refresh balance so the hero section shows updated tokens
            const payload = JSON.parse(atob(authToken.split('.')[1]));
            const uid = payload.email || payload.sub;
            const balRes = await fetch(`${BILLING_API}/balance?user_id=${uid}`, {
              headers: { Authorization: authToken },
            });
            if (balRes.ok) {
              const balData = await balRes.json();
              setBalance(balData.balance);
              setLifetimePurchased(balData.lifetimePurchased || 1);
            }
          } else {
            setToastMessage('Payment successful! Your tokens have been added.');
          }
        } catch (e) {
          console.error('Session verify failed:', e);
          setToastMessage('Payment successful! Your tokens have been added.');
        }

        // Redirect back to where user came from, with sidebar open
        if (returnTo) {
          localStorage.removeItem('bytesized_billing_return');
          const dest = returnTo.includes('?') ? `${returnTo}&sidebar=open` : `${returnTo}?sidebar=open`;
          setTimeout(() => navigate(dest), 2000);
        }
      })();

      window.history.replaceState({}, '', window.location.pathname);
    } else if (payment === 'success') {
      setToastMessage('Payment successful! Your tokens have been added.');
      window.history.replaceState({}, '', window.location.pathname);
      if (returnTo) {
        localStorage.removeItem('bytesized_billing_return');
        const dest = returnTo.includes('?') ? `${returnTo}&sidebar=open` : `${returnTo}?sidebar=open`;
        setTimeout(() => navigate(dest), 2000);
      }
    } else if (payment === 'cancelled') {
      setToastMessage('Payment cancelled.');
      localStorage.removeItem('bytesized_billing_return');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Fetch auth session and extract userId from JWT
  useEffect(() => {
    const getSession = async () => {
      try {
        const { tokens } = await fetchAuthSession();
        if (tokens?.idToken) {
          const idToken = tokens.idToken.toString();
          setToken(idToken);
          const payload = JSON.parse(atob(idToken.split('.')[1]));
          setUserId(payload.email || payload.sub);
        } else {
          navigate('/login');
        }
      } catch (err) {
        console.error('Error fetching auth session:', err);
        navigate('/login');
      }
    };
    getSession();
  }, [navigate]);

  // Fetch everything once authenticated
  useEffect(() => {
    if (!token || !userId) return;
    const headers = { Authorization: token };

    fetch(`${BILLING_API}/balance?user_id=${userId}`, { headers })
      .then(r => r.json())
      .then(data => {
        setBalance(data.balance);
        setLifetimePurchased(data.lifetimePurchased || 1);
        setCurrentPack(data.currentPack || 'free');
      })
      .catch(() => {});

    fetch(`${BILLING_API}/token-costs`, { headers })
      .then(r => r.json())
      .then(data => setTokenCosts(data))
      .catch(() => {});

    fetch(`${BILLING_API}/flow-usage?user_id=${userId}`, { headers })
      .then(r => r.json())
      .then(data => setFlowUsage(data))
      .catch(() => setFlowUsage({ flowUsage: [], unattributed: { totalSpent: 0, actions: {} } }));

    fetch(`${API_BASE}/v1/flowdecomposition/agents/list?user_id=${userId}`, { headers })
      .then(r => r.json())
      .then(data => {
        const map = {};
        if (Array.isArray(data)) {
          data.forEach(a => {
            const id = a.agent_id || a.agentId;
            const name = a.concept_name || a.conceptName || a.name || id;
            if (id) map[id] = name;
          });
        }
        setAgentNames(map);
      })
      .catch(() => {});
  }, [token, userId]);

  // Fetch transactions when history is expanded
  useEffect(() => {
    if (!historyOpen || transactions.length > 0 || !token || !userId) return;
    setLoadingTx(true);
    fetch(`${BILLING_API}/transactions?user_id=${userId}&limit=50`, {
      headers: { Authorization: token },
    })
      .then(r => r.json())
      .then(data => setTransactions(data.transactions || []))
      .catch(() => {})
      .finally(() => setLoadingTx(false));
  }, [historyOpen, token, userId]);

  // Packs modal handlers
  const closePacksModal = useCallback(() => {
    setPacksModalOpen(false);
    setTimeout(() => {
      setSelectedPack(null);
      setPacksModalStep('select');
      setPacks([]);
      setCheckoutLoading(false);
    }, 300);
  }, []);

  const openPacksModal = () => {
    setPacksModalStep('select');
    setSelectedPack(null);
    setPacksError(null);
    setRegionPickerOpen(false);
    setPacksModalOpen(true);
  };

  // Auto-open recharge packs when navigated with ?recharge=true
  useEffect(() => {
    if (searchParams.get('recharge') === 'true') {
      openPacksModal();
      searchParams.delete('recharge');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectPack = (pack) => {
    setSelectedPack(pack);
    setPacksModalStep('confirm');
  };

  const handleBackToSelect = () => {
    setPacksModalStep('select');
    setSelectedPack(null);
  };

  const handleProceedToPayment = async () => {
    if (!selectedPack || checkoutLoading) return;

    setCheckoutLoading(true);

    if (selectedRegion === 'india') {
      // ── Razorpay flow (modal-based, no redirect) ──
      try {
        const res = await fetch(`${BILLING_API}/create-razorpay-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: token },
          body: JSON.stringify({
            userId: userId,
            pack: selectedPack.name,
            region: selectedRegion,
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Failed to create order');
        }
        const { orderId, amount, currency, keyId } = await res.json();
        const packColor = PACK_COLORS[selectedPack.name]?.accent || '#6366f1';

        const options = {
          key: keyId,
          amount,
          currency,
          order_id: orderId,
          name: 'BytesizedAI',
          description: `${PACK_COLORS[selectedPack.name]?.label || selectedPack.name} Pack`,
          handler: async (response) => {
            // Payment succeeded — verify on backend
            try {
              const verifyRes = await fetch(`${BILLING_API}/verify-razorpay-payment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: token },
                body: JSON.stringify({
                  razorpayOrderId: response.razorpay_order_id,
                  razorpayPaymentId: response.razorpay_payment_id,
                  razorpaySignature: response.razorpay_signature,
                  userId: userId,
                  pack: selectedPack.name,
                  region: selectedRegion,
                }),
              });
              if (!verifyRes.ok) {
                const err = await verifyRes.json().catch(() => ({}));
                throw new Error(err.detail || 'Payment verification failed');
              }
              const result = await verifyRes.json();
              // Refresh balance
              const balRes = await fetch(`${BILLING_API}/balance?user_id=${userId}`, {
                headers: { Authorization: token },
              });
              if (balRes.ok) {
                const balData = await balRes.json();
                setBalance(balData.balance);
                setLifetimePurchased(balData.lifetimePurchased || 1);
                setCurrentPack(balData.currentPack || 'free');
              }
              setToastMessage(
                result.status === 'already_processed'
                  ? 'Payment already processed.'
                  : `Payment successful! +${result.tokens_added || result.tokensAdded} tokens added.`
              );
              closePacksModal();
            } catch (e) {
              setToastMessage(e.message || 'Payment verification failed');
            } finally {
              setCheckoutLoading(false);
            }
          },
          modal: {
            ondismiss: () => setCheckoutLoading(false),
          },
          theme: { color: packColor },
          prefill: { email: userId },
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (resp) => {
          setToastMessage(resp.error?.description || 'Payment failed — please try again');
          setCheckoutLoading(false);
        });
        rzp.open();
      } catch (e) {
        setToastMessage(e.message || 'Payment failed — please try again');
        setCheckoutLoading(false);
      }
      return;
    }

    // ── Stripe flow (redirect-based) ──
    try {
      const res = await fetch(`${BILLING_API}/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({
          userId: userId,
          pack: selectedPack.name,
          region: selectedRegion,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create checkout');
      }
      const data = await res.json();
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (e) {
      setToastMessage(e.message || 'Payment failed — please try again');
      setCheckoutLoading(false);
    }
  };

  // Fetch packs when modal opens or region changes
  useEffect(() => {
    if (!packsModalOpen || !token) return;
    setPacksLoading(true);
    setPacksError(null);
    fetch(`${BILLING_API}/packs?region=${selectedRegion}`, {
      headers: { Authorization: token },
    })
      .then(r => { if (!r.ok) throw new Error('Failed to load packs'); return r.json(); })
      .then(data => setPacks(data.packs || []))
      .catch(e => setPacksError(e.message))
      .finally(() => setPacksLoading(false));
  }, [packsModalOpen, selectedRegion, token]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(null), 3000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  // Load Razorpay SDK script
  useEffect(() => {
    if (document.getElementById('razorpay-checkout-js')) return;
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  // Lock body scroll + close on Escape
  useEffect(() => {
    if (!packsModalOpen) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') closePacksModal(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [packsModalOpen, closePacksModal]);

  // Computed
  const flowBudget = tokenCosts
    ? Object.keys(EST_USES).reduce((sum, a) => sum + (tokenCosts[a] || 0) * EST_USES[a], 0)
    : 50;
  const flowsRemaining = balance !== null ? Math.floor(balance / flowBudget) : null;

  const getFuelColor = () => {
    if (balance === null || lifetimePurchased === null) return dark ? '#6b7280' : '#9ca3af';
    const pct = balance / lifetimePurchased;
    if (pct > 0.5) return '#22c55e';
    if (pct > 0.2) return '#eab308';
    return '#ef4444';
  };

  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  /* ─── Build carousel slides ─── */
  const buildSlides = () => {
    if (!flowUsage) return [];
    const slides = [];

    const hasFlows = flowUsage.flowUsage?.length > 0;
    const hasUnattributed = flowUsage.unattributed?.totalSpent > 0;

    if (!hasFlows && !hasUnattributed) {
      // No usage yet — show empty budget template
      slides.push({ type: 'empty', title: 'Flow budget' });
      return slides;
    }

    // Summary slide when multiple flows exist
    if (flowUsage.flowUsage?.length > 1) {
      const totalSpent = flowUsage.flowUsage.reduce((s, f) => s + f.totalSpent, 0)
        + (flowUsage.unattributed?.totalSpent || 0);
      slides.push({
        type: 'summary',
        title: 'All Flows',
        totalFlows: flowUsage.flowUsage.length,
        totalSpent,
      });
    }

    // One slide per flow
    flowUsage.flowUsage?.forEach(flow => {
      slides.push({
        type: 'flow',
        agentId: flow.agentId,
        title: agentNames[flow.agentId] || flow.agentId,
        totalSpent: flow.totalSpent,
        actions: flow.actions,
      });
    });

    // Unattributed slide
    if (hasUnattributed) {
      slides.push({
        type: 'unattributed',
        title: 'Unattributed',
        totalSpent: flowUsage.unattributed.totalSpent,
        actions: flowUsage.unattributed.actions,
      });
    }

    return slides;
  };

  const slides = buildSlides();
  const safeIndex = Math.min(carouselIndex, Math.max(slides.length - 1, 0));

  /* ─── Render carousel ─── */
  const renderCarousel = () => {
    if (flowUsage === null) {
      return (
        <div className={`text-xs text-center py-6 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
          Loading...
        </div>
      );
    }

    const slide = slides[safeIndex];
    if (!slide) return null;

    return (
      <div>
        {/* Carousel nav arrows */}
        {slides.length > 1 && (
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setCarouselIndex(Math.max(0, safeIndex - 1))}
              disabled={safeIndex === 0}
              className={`p-1 rounded-lg transition-all ${
                safeIndex === 0
                  ? (dark ? 'text-gray-700' : 'text-gray-300')
                  : (dark ? 'text-gray-400 hover:text-cyan-400 hover:bg-white/5'
                          : 'text-gray-500 hover:text-cyan-600 hover:bg-black/5')
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <span className={`text-sm font-medium ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
              {slide.title}
              <span className={`ml-2 ${dark ? 'text-gray-600' : 'text-gray-400'}`}>
                ({safeIndex + 1}/{slides.length})
              </span>
            </span>

            <button
              onClick={() => setCarouselIndex(Math.min(slides.length - 1, safeIndex + 1))}
              disabled={safeIndex >= slides.length - 1}
              className={`p-1 rounded-lg transition-all ${
                safeIndex >= slides.length - 1
                  ? (dark ? 'text-gray-700' : 'text-gray-300')
                  : (dark ? 'text-gray-400 hover:text-cyan-400 hover:bg-white/5'
                          : 'text-gray-500 hover:text-cyan-600 hover:bg-black/5')
              }`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Slide content */}
        {slide.type === 'empty' ? (
          <div>
            <FlowBudgetCard
              title="Flow budget"
              totalSpent={0}
              actions={{}}
              tokenCosts={tokenCosts}
              flowBudget={flowBudget}
              dark={dark}
            />
            <div className={`text-xs text-center mt-3 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
              Create your first flow to see usage here
            </div>
          </div>
        ) : slide.type === 'summary' ? (
          <div>
            <div className={`text-center mb-4 pb-3 border-b ${dark ? 'border-white/10' : 'border-gray-200/50'}`}>
              <span className={`text-2xl font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>
                {slide.totalSpent}
              </span>
              <span className={`text-xs ml-2 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                tokens across {slide.totalFlows} flow{slide.totalFlows !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="space-y-2">
              {flowUsage.flowUsage.map(flow => (
                <button
                  key={flow.agentId}
                  onClick={() => {
                    const idx = slides.findIndex(s => s.type === 'flow' && s.agentId === flow.agentId);
                    if (idx >= 0) setCarouselIndex(idx);
                  }}
                  className={`w-full flex items-center justify-between py-2 px-3 rounded-lg transition-colors ${
                    dark ? 'bg-white/5 hover:bg-white/10' : 'bg-black/5 hover:bg-black/8'
                  }`}
                >
                  <span className={`text-xs font-medium ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {agentNames[flow.agentId] || flow.agentId}
                  </span>
                  <span className={`text-xs tabular-nums font-medium ${dark ? 'text-cyan-400' : 'text-cyan-600'}`}>
                    {flow.totalSpent}/{flowBudget} tokens
                  </span>
                </button>
              ))}
              {flowUsage.unattributed?.totalSpent > 0 && (
                <button
                  onClick={() => {
                    const idx = slides.findIndex(s => s.type === 'unattributed');
                    if (idx >= 0) setCarouselIndex(idx);
                  }}
                  className={`w-full flex items-center justify-between py-2 px-3 rounded-lg transition-colors ${
                    dark ? 'bg-white/5 hover:bg-white/10' : 'bg-black/5 hover:bg-black/8'
                  }`}
                >
                  <span className={`text-xs font-medium ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Unattributed
                  </span>
                  <span className={`text-xs tabular-nums ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {flowUsage.unattributed.totalSpent} tokens
                  </span>
                </button>
              )}
            </div>
          </div>
        ) : (
          <FlowBudgetCard
            title={slide.title}
            totalSpent={slide.totalSpent}
            actions={slide.actions}
            tokenCosts={tokenCosts}
            flowBudget={flowBudget}
            dark={dark}
          />
        )}

        {/* Dot indicators */}
        {slides.length > 1 && (
          <div className="flex justify-center items-center space-x-1.5 mt-4">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCarouselIndex(i)}
                className={`rounded-full transition-all duration-200 ${
                  i === safeIndex
                    ? 'bg-cyan-400 h-1.5 w-4'
                    : `h-1.5 w-1.5 ${dark ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-300 hover:bg-gray-400'}`
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  /* ─── Pack modal render helpers ─── */
  const regionCfg = REGION_CONFIG[selectedRegion];

  const renderRegionLine = () => (
    <div className="flex items-center justify-center gap-2 mb-5">
      <Globe className={`w-3.5 h-3.5 ${dark ? 'text-gray-500' : 'text-gray-400'}`} />
      <span className={`text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
        {regionCfg.flag} Pricing in {regionCfg.currency} &middot; {regionCfg.gateway}
      </span>
      {!regionPickerOpen ? (
        <button
          onClick={() => setRegionPickerOpen(true)}
          className={`text-[11px] underline underline-offset-2 transition-colors ${
            dark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          Change
        </button>
      ) : (
        <div className="flex items-center gap-1">
          {Object.entries(REGION_CONFIG).filter(([k]) => k !== selectedRegion).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => { setSelectedRegion(key); setRegionPickerOpen(false); setPacksError(null); }}
              className={`px-2 py-0.5 rounded-full text-[11px] font-medium transition-all duration-200 ${
                dark
                  ? 'text-gray-300 hover:bg-white/10 bg-white/5'
                  : 'text-gray-600 hover:bg-black/10 bg-black/5'
              }`}
            >
              {cfg.flag} {cfg.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderPacksSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className={`rounded-xl p-4 animate-pulse ${
            dark ? 'bg-white/5' : 'bg-black/5'
          }`}
          style={{ height: '260px' }}
        />
      ))}
    </div>
  );

  const renderPackCards = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      {packs.map(pack => {
        const color = PACK_COLORS[pack.name] || { accent: '#6b7280', label: pack.name, features: [] };
        const isPro = pack.name === 'pro';

        return (
          <SpotlightCard
            key={pack.name}
            spotlightColor={`${color.accent}40`}
            className={`rounded-xl border cursor-pointer transition-all duration-200 hover:scale-[1.02] ${
              isPro
                ? (dark
                    ? 'bg-white/[0.06] border-purple-500/30 hover:border-purple-400/50 ring-1 ring-purple-500/20'
                    : 'bg-purple-50/60 border-purple-200 hover:border-purple-300 ring-1 ring-purple-200/50')
                : (dark
                    ? 'bg-white/[0.04] border-white/10 hover:border-white/20'
                    : 'bg-white/60 border-gray-200/60 hover:border-gray-300')
            }`}
          >
            <button
              onClick={() => handleSelectPack(pack)}
              className="w-full h-full text-left p-4 relative flex flex-col"
            >
              {/* Popular badge */}
              {isPro && (
                <span
                  className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase"
                  style={{ background: `${color.accent}25`, color: color.accent }}
                >
                  Popular
                </span>
              )}

              {/* Pack name */}
              <div className="flex items-center gap-1.5 mb-2">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ background: color.accent, boxShadow: `0 0 8px ${color.accent}60` }}
                />
                <span className={`text-sm font-semibold ${dark ? 'text-gray-100' : 'text-gray-800'}`}>
                  {color.label}
                </span>
              </div>

              {/* Flows */}
              <div className="flex items-baseline gap-1 mb-0.5">
                <span className={`text-2xl font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>
                  {pack.flows}
                </span>
                <span className={`text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                  flow{pack.flows !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Price */}
              <div className={`text-base font-semibold ${dark ? 'text-gray-200' : 'text-gray-700'}`}>
                {regionCfg.currency}{pack.priceLocal.toLocaleString()}
              </div>

              {/* Per-flow + savings */}
              <div className="flex items-center gap-1.5 mb-3">
                <span className={`text-[11px] ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {regionCfg.currency}{pack.perFlowPrice}/flow
                </span>
                {pack.savingsPct && (
                  <span
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                    style={{ background: '#22c55e20', color: '#22c55e' }}
                  >
                    {pack.savingsPct}% off
                  </span>
                )}
              </div>

              {/* Feature list */}
              <div className={`mt-auto pt-3 border-t space-y-1.5 ${dark ? 'border-white/5' : 'border-gray-200/40'}`}>
                {(color.features || []).map((feat, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <span className="text-[10px] mt-[3px] flex-shrink-0" style={{ color: color.accent }}>&#x2713;</span>
                    <span className={`text-[11px] leading-tight ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {feat}
                    </span>
                  </div>
                ))}
              </div>
            </button>
          </SpotlightCard>
        );
      })}
    </div>
  );

  const renderConfirmStep = () => {
    if (!selectedPack) return null;
    const color = PACK_COLORS[selectedPack.name] || { accent: '#6b7280', label: selectedPack.name };

    return (
      <div className="max-w-md mx-auto space-y-6">
        {/* Back button */}
        <button
          onClick={handleBackToSelect}
          className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
            dark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Back to packs
        </button>

        {/* Selected pack summary */}
        <div
          className={`rounded-xl border p-6 ${
            dark ? 'bg-white/[0.04] border-white/10' : 'bg-white/60 border-gray-200/60'
          }`}
        >
          <div className="text-center">
            <div
              className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
              style={{ background: `${color.accent}20` }}
            >
              <Package className="w-6 h-6" style={{ color: color.accent }} />
            </div>
            <h3 className={`text-lg font-bold mb-1 ${dark ? 'text-white' : 'text-gray-900'}`}>
              {color.label} Pack
            </h3>
            <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
              {selectedPack.flows} flow{selectedPack.flows !== 1 ? 's' : ''} &middot; {regionCfg.currency}{selectedPack.priceLocal.toLocaleString()}
            </p>
            {selectedPack.savingsPct && (
              <span
                className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full mt-2"
                style={{ background: '#22c55e20', color: '#22c55e' }}
              >
                You save {selectedPack.savingsPct}% vs Lite
              </span>
            )}
          </div>

          {/* Features */}
          {(color.features || []).length > 0 && (
            <div className={`mt-4 pt-4 border-t ${dark ? 'border-white/5' : 'border-gray-200/40'}`}>
              <p className={`text-xs font-medium mb-2.5 ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
                What's included
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {color.features.map((feat, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs flex-shrink-0" style={{ color: color.accent }}>&#x2713;</span>
                    <span className={`text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {feat}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Gateway info */}
        <div
          className={`rounded-xl border p-4 flex items-center gap-3 ${
            dark ? 'bg-white/[0.03] border-white/10' : 'bg-white/50 border-gray-200/50'
          }`}
        >
          <CreditCard className={`w-5 h-5 flex-shrink-0 ${dark ? 'text-gray-400' : 'text-gray-500'}`} />
          <div>
            <p className={`text-sm font-medium ${dark ? 'text-gray-200' : 'text-gray-700'}`}>
              Payment via {regionCfg.gateway}
            </p>
            <p className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
              {selectedRegion === 'india'
                ? 'UPI, Cards, Net Banking, Wallets'
                : 'Credit / Debit Card'}
            </p>
          </div>
        </div>

        {/* Proceed button */}
        <button
          onClick={handleProceedToPayment}
          disabled={checkoutLoading}
          className={`w-full py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 ${
            checkoutLoading ? 'opacity-70 cursor-wait' : 'hover:brightness-110 active:scale-[0.98]'
          }`}
          style={{
            background: `linear-gradient(135deg, ${color.accent}, ${color.accent}cc)`,
            boxShadow: `0 4px 20px ${color.accent}40`,
          }}
        >
          <span className="flex items-center justify-center gap-2">
            {checkoutLoading ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {selectedRegion === 'india' ? 'Processing payment...' : 'Redirecting to Stripe...'}
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Proceed to Payment
              </>
            )}
          </span>
        </button>
      </div>
    );
  };

  return (
    <div className={`min-h-screen relative overflow-hidden ${dark ? 'bg-[#030d1a]' : 'bg-[#e8f4f8]'}`}>
      {/* Background */}
      <div className="fixed inset-0 z-0 pointer-events-none" style={{ opacity: dark ? 1 : 0.3 }}>
        <FloatingLines
          linesGradient={dark
            ? ['#0e7490', '#0891b2', '#06b6d4', '#22d3ee', '#67e8f9', '#0ea5e9']
            : ['#0077b6', '#0096c7', '#00b4d8', '#48cae4', '#0077b6', '#00b4d8']
          }
          enabledWaves={['top', 'middle', 'bottom']}
          lineCount={[5, 4, 3]}
          lineDistance={[4, 5, 3]}
          animationSpeed={0.5}
          interactive={true}
          bendRadius={5.0}
          bendStrength={-0.5}
          mouseDamping={0.04}
          parallax={true}
          parallaxStrength={0.15}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className={`flex items-center px-6 py-4 border-b backdrop-blur-xl ${
          dark ? 'border-white/10 bg-gray-950/50' : 'border-gray-300/30 bg-white/50'
        }`}>
          <div className="flex-1 flex justify-start">
            <button
              onClick={() => {
                const returnTo = localStorage.getItem('bytesized_billing_return');
                localStorage.removeItem('bytesized_billing_return');
                navigate(returnTo || '/');
              }}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all duration-300 text-sm font-medium ${
                dark
                  ? 'text-gray-300 hover:text-cyan-400 hover:bg-cyan-900/30'
                  : 'text-gray-600 hover:text-cyan-600 hover:bg-cyan-50'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
          </div>

          <h1 className="text-lg font-semibold">
            <GradientText
              colors={dark
                ? ['#22d3ee', '#67e8f9', '#a5f3fc', '#22d3ee']
                : ['#064e5e', '#0e7490', '#0891b2', '#064e5e']
              }
              animationSpeed={6}
            >
              Billing
            </GradientText>
          </h1>

          <div className="flex-1 flex justify-end gap-2">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-all duration-300 ${
                dark
                  ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            <button
              onClick={handleLogout}
              className={`p-2 rounded-lg transition-all duration-300 ${
                dark
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-400 hover:text-red-400'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-500 hover:text-red-500'
              }`}
              title="Sign out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Main content */}
        <div className="flex-1 flex flex-col items-center px-4 py-8 overflow-y-auto">
          <div className="w-full max-w-2xl space-y-6">

            {/* Hero fuel gauge */}
            <div className={`rounded-2xl p-8 text-center backdrop-blur-xl border relative ${
              dark
                ? 'bg-gray-900/60 border-white/10'
                : 'bg-white/60 border-gray-200/50'
            }`}>
              <button
                className={`absolute top-4 right-4 px-4 py-1.5 rounded-lg text-xs font-medium transition-all duration-300 border ${
                  dark
                    ? 'bg-cyan-900/30 hover:bg-cyan-800/40 text-cyan-300 border-cyan-700/50 hover:border-cyan-600'
                    : 'bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border-cyan-200 hover:border-cyan-300'
                }`}
                onClick={openPacksModal}
              >
                Add Credits
              </button>
              <div className="flex flex-col items-center mb-4">
                <div
                  className="rounded-full p-4"
                  style={{
                    background: `${getFuelColor()}20`,
                    boxShadow: `0 0 30px ${getFuelColor()}30`,
                  }}
                >
                  <Fuel className="w-12 h-12" style={{ color: getFuelColor() }} />
                </div>
              </div>
              <h2 className={`text-3xl font-bold mb-2 ${dark ? 'text-white' : 'text-gray-900'}`}>
                {flowsRemaining !== null ? flowsRemaining : '\u2014'}
              </h2>
              <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                {flowsRemaining !== null
                  ? `flow${flowsRemaining !== 1 ? 's' : ''} in the tank`
                  : 'Loading balance...'}
              </p>
              {currentPack && (
                <span className={`inline-block mt-3 px-3 py-0.5 rounded-full text-xs font-semibold ${
                  currentPack === 'free'
                    ? (dark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600')
                    : (dark ? 'bg-cyan-900/40 text-cyan-300 border border-cyan-700/50' : 'bg-cyan-50 text-cyan-700 border border-cyan-200')
                }`}>
                  {currentPack.charAt(0).toUpperCase() + currentPack.slice(1)}
                </span>
              )}
              {balance !== null && (
                <p className={`text-xs mt-2 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {balance} tokens remaining
                </p>
              )}
            </div>

            {/* Flow usage carousel */}
            <div className={`rounded-2xl p-6 backdrop-blur-xl border ${
              dark
                ? 'bg-gray-900/60 border-white/10'
                : 'bg-white/60 border-gray-200/50'
            }`}>
              <h3 className={`text-base font-semibold mb-4 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
                Token usage
              </h3>
              {renderCarousel()}
            </div>

            {/* Transaction history (expandable) */}
            <div className={`rounded-2xl backdrop-blur-xl border overflow-hidden ${
              dark
                ? 'bg-gray-900/60 border-white/10'
                : 'bg-white/60 border-gray-200/50'
            }`}>
              <button
                onClick={() => setHistoryOpen(!historyOpen)}
                className={`w-full flex items-center justify-between px-6 py-4 transition-colors ${
                  dark ? 'hover:bg-white/5' : 'hover:bg-black/5'
                }`}
              >
                <span className={`text-sm font-semibold ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
                  View usage history
                </span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-300 ${
                    historyOpen ? 'rotate-180' : ''
                  } ${dark ? 'text-gray-400' : 'text-gray-500'}`}
                />
              </button>

              {historyOpen && (
                <div className={`px-6 pb-4 border-t ${dark ? 'border-white/5' : 'border-gray-200/30'}`}>
                  {loadingTx ? (
                    <p className={`text-xs text-center py-4 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                      Loading transactions...
                    </p>
                  ) : transactions.length === 0 ? (
                    <p className={`text-xs text-center py-4 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                      No transactions yet.
                    </p>
                  ) : (
                    <div className="overflow-x-auto mt-3">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className={`border-b ${dark ? 'border-white/10' : 'border-gray-200/50'}`}>
                            {['Date', 'Action', 'Amount', 'Balance'].map(h => (
                              <th
                                key={h}
                                className={`text-left py-2 pr-3 font-medium ${
                                  dark ? 'text-gray-400' : 'text-gray-500'
                                }`}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((tx, i) => (
                            <tr
                              key={tx.transactionId || i}
                              className={`border-b last:border-0 ${
                                dark ? 'border-white/5' : 'border-gray-100'
                              }`}
                            >
                              <td className={`py-2 pr-3 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {formatRelativeTime(tx.timestamp)}
                              </td>
                              <td className={`py-2 pr-3 font-medium ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
                                {ACTION_LABELS[tx.action] || tx.action}
                              </td>
                              <td className={`py-2 pr-3 tabular-nums ${
                                tx.tokens > 0
                                  ? 'text-green-400'
                                  : tx.tokens < 0 ? 'text-red-400' : (dark ? 'text-gray-400' : 'text-gray-500')
                              }`}>
                                {tx.tokens > 0 ? '+' : ''}{tx.tokens}
                              </td>
                              <td className={`py-2 tabular-nums ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {tx.balanceAfter}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* ─── Packs Modal ─── */}
      <AnimatePresence>
        {packsModalOpen && (
          <motion.div
            key="packs-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={closePacksModal}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            {/* Content */}
            <motion.div
              key="packs-content"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              onClick={e => e.stopPropagation()}
              className={`relative w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl border p-6 ${
                dark
                  ? 'bg-gray-900/95 backdrop-blur-2xl border-white/10'
                  : 'bg-white/95 backdrop-blur-2xl border-gray-200/60'
              }`}
            >
              {/* Close button */}
              <button
                onClick={closePacksModal}
                className={`absolute top-4 right-4 p-1.5 rounded-lg transition-colors ${
                  dark ? 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                       : 'text-gray-400 hover:text-gray-600 hover:bg-black/5'
                }`}
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="text-center mb-5">
                <h2 className={`text-xl font-bold mb-1 ${dark ? 'text-white' : 'text-gray-900'}`}>
                  {packsModalStep === 'select' ? 'Recharge Packs' : 'Confirm Pack'}
                </h2>
                {packsModalStep === 'select' && (
                  <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Prepaid flow packs — use at your own pace
                  </p>
                )}
              </div>

              {/* Body */}
              {packsModalStep === 'select' ? (
                <>
                  {renderRegionLine()}
                  {packsError ? (
                    <div className={`text-center py-8 text-sm ${dark ? 'text-red-400' : 'text-red-500'}`}>
                      {packsError}
                    </div>
                  ) : packsLoading ? (
                    renderPacksSkeleton()
                  ) : (
                    renderPackCards()
                  )}
                </>
              ) : (
                renderConfirmStep()
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Toast ─── */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            key="toast"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-5 py-2.5 rounded-xl text-sm font-medium shadow-lg ${
              dark
                ? 'bg-gray-800 text-gray-100 border border-white/10'
                : 'bg-gray-900 text-white'
            }`}
          >
            {toastMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default BillingPage;
